import { useLayoutEffect, useRef } from 'react'

import { toolbarHeightCssVar } from '@sales-center/common'

/**
 * Publishes the toolbar's measured height on `<html>` as
 * `--sc-toolbar-height` so every layout offset that has to clear the fixed
 * toolbar can read it instead of hardcoding `78px`.
 *
 * The toolbar stacks the logo above the action icons below the `sm`
 * breakpoint (`height: { xs: 'auto', sm: toolbarHeight }`), which takes it from
 * 78px to ~125px. Consumers that assumed a constant 78px — most visibly the
 * sticky form header in `SettingsPage` — ended up rendering *underneath* it.
 *
 * Returns the ref to attach to the toolbar root.
 */
export const usePublishToolbarHeight = () => {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    const publish = () => {
      document.documentElement.style.setProperty(
        toolbarHeightCssVar,
        `${element.getBoundingClientRect().height}px`,
      )
    }

    publish()

    const observer = new ResizeObserver(publish)
    observer.observe(element)

    return () => {
      observer.disconnect()
      // The toolbar is unmounted on logout and on /access-denied, where no
      // toolbar is rendered at all. Drop the override so consumers fall back
      // to the `toolbarHeight` default baked into `toolbarHeightVar`.
      document.documentElement.style.removeProperty(toolbarHeightCssVar)
    }
  }, [])

  return ref
}
